import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { opsApi } from '@/api/ops';
import type { LocalizedText, PackSummary } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { StatusChip } from '@/components/ops/Badges';
import { KeyValues, PageHeader, Section } from '@/components/ops/Layout';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { QueryState } from '@/components/ops/Problem';
import { REPLICA } from '@/ops/live';

const PACKS_KEY = ['ops', 'packs'] as const;

function text(value: LocalizedText | null | undefined): string {
  return value?.en ?? value?.ar ?? '—';
}

/** The industry packs a job can apply (D-CP9): code-as-data JSON shipped with the console. */
export function IndustryPacksPage() {
  const packs = useQuery({ queryKey: PACKS_KEY, queryFn: opsApi.packs.list, ...REPLICA, staleTime: 5 * 60_000 });
  return (
    <RequirePermission permission={PERMISSIONS.tenantProvision} what="Industry packs">
      <PageHeader title="Industry packs"
        subtitle="Each section runs as its own industry.<section> step through the owning service's endpoint; a missing endpoint is skipped, never 'done'." />
      <QueryState query={packs}>
        {(data) => data.packs.length === 0 ? (
          <div className="empty-state t-body">This console ships no industry packs.</div>
        ) : (
          <div className="pack-grid">
            {data.packs.map((p) => <PackCard key={p.key} pack={p} />)}
          </div>
        )}
      </QueryState>
    </RequirePermission>
  );
}

function PackCard({ pack: p }: { pack: PackSummary }) {
  return (
    <Link to={`/ops/industry-packs/${encodeURIComponent(p.key)}`} className="pack-option" style={{ textDecoration: 'none' }}>
      <span className="row" style={{ justifyContent: 'space-between' }}>
        <strong className="t-heading">{text(p.label)}</strong>
        <span className="row">
          {p.retired && <StatusChip status="retired" tone="warning" title="Old setups still retry with it; new tenants cannot start from it." />}
          {p.valid ? <StatusChip status={`v${p.version ?? '?'}`} tone="muted" /> : <StatusChip status="invalid" tone="danger" />}
        </span>
      </span>
      {p.label?.ar && <span className="t-caption muted" dir="rtl">{p.label.ar}</span>}
      <span className="t-caption muted">{text(p.description)}</span>
      <span className="row">{p.sections.map((s) => <span key={s} className="chip chip-info">{s}</span>)}</span>
      <span className="t-caption subtle mono">{p.key}</span>
    </Link>
  );
}

export function IndustryPackDetailPage() {
  const { key = '' } = useParams();
  const pack = useQuery({ queryKey: [...PACKS_KEY, key], queryFn: () => opsApi.packs.get(key), ...REPLICA });

  return (
    <RequirePermission permission={PERMISSIONS.tenantProvision} what="Industry packs">
      <QueryState query={pack}>
        {({ summary: s, pack: doc }) => (
          <>
            <PageHeader back={{ to: '/ops/industry-packs', label: 'Industry packs' }} title={text(s.label)}
              subtitle={<span className="row"><span className="mono">{s.key}</span><span>v{s.version ?? '?'}</span>{s.valid ? <StatusChip status="valid" tone="success" /> : <StatusChip status="invalid" tone="danger" />}</span>} />
            {!s.valid && (
              <div className="callout callout-error" role="alert">
                <span className="callout-icon" aria-hidden="true">✕</span>
                <div className="t-body">
                  <strong className="t-heading" style={{ display: 'block' }}>This pack fails validation — a job cannot use it</strong>
                  <ul className="problem-list">{s.errors.map((e) => <li key={e}>{e}</li>)}</ul>
                </div>
              </div>
            )}
            <Section title="Summary">
              <KeyValues rows={[
                { label: 'Description', value: <>{text(s.description)}{s.description?.ar && <div className="t-caption muted" dir="rtl">{s.description.ar}</div>}</> },
                { label: 'Steps', value: <span className="row">{s.sections.map((x) => <span key={x} className="chip chip-info mono">industry.{x}</span>)}</span> },
                { label: 'Metadata seeder', value: <span className="mono">{s.seeder ?? '—'}</span> },
                { label: 'Features', value: s.features.join(', ') || '—' },
                { label: 'Relabelled modules', value: s.relabels.join(', ') || '—' },
                { label: 'Extra modules', value: s.extra_modules.join(', ') || '—' },
                { label: 'AI industry', value: s.ai_industry ?? '—' },
                { label: 'Intent taxonomy', value: s.intent_taxonomy.join(', ') || '—' },
                { label: 'Skills pack', value: s.skills_pack ?? '—' },
                { label: 'SalesIQ departments', value: s.departments.map((d) => text(d)).join(', ') || '—' },
                { label: 'Agents', value: s.agents_optional === null ? '—' : s.agents_optional ? 'optional' : 'required' },
              ]} />
              {s.notes.length > 0 && <ul className="problem-list t-caption">{s.notes.map((n) => <li key={n}>{n}</li>)}</ul>}
            </Section>
            {doc && <PackDocument doc={doc} />}
          </>
        )}
      </QueryState>
    </RequirePermission>
  );
}

interface PackField { key: string; type: string; label?: LocalizedText | null; options?: string[] | null; lookup_module?: string | null; required?: boolean | null }
interface PackModule { key: string; label?: LocalizedText | null; plural?: LocalizedText | null; icon?: string | null; section?: LocalizedText | null; fields?: PackField[] | null }

function FieldsTable({ fields }: { fields: PackField[] }) {
  return (
    <table className="table">
      <thead><tr><th>Field</th><th>Type</th><th>Label (en · ar)</th><th>Detail</th></tr></thead>
      <tbody>
        {fields.map((f) => (
          <tr key={f.key}>
            <td className="mono">{f.key}{f.required && ' *'}</td>
            <td className="mono t-caption">{f.type}</td>
            <td>{f.label?.en ?? '—'}<div className="t-caption muted" dir="rtl">{f.label?.ar ?? ''}</div></td>
            <td className="t-caption">{f.lookup_module ? `→ ${f.lookup_module}` : (f.options ?? []).join(', ') || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The whole pack document — what each section step will send, rendered read-only. */
function PackDocument({ doc }: { doc: Record<string, unknown> }) {
  const metadata = (doc.metadata ?? null) as {
    relabels?: Record<string, LocalizedText & { singular?: LocalizedText }>;
    extra_modules?: PackModule[];
    extra_fields?: Record<string, { section?: LocalizedText; fields?: PackField[] }>;
    folders?: { name?: LocalizedText; modules?: string[] }[];
  } | null;

  return (
    <>
      {metadata?.relabels && Object.keys(metadata.relabels).length > 0 && (
        <Section title="Relabels" flush>
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Module</th><th>Plural (en · ar)</th><th>Singular</th></tr></thead>
              <tbody>
                {Object.entries(metadata.relabels).map(([module, r]) => (
                  <tr key={module}>
                    <td className="mono">{module}</td>
                    <td>{r.en ?? '—'}<div className="t-caption muted" dir="rtl">{r.ar ?? ''}</div></td>
                    <td>{r.singular?.en ?? '—'}<div className="t-caption muted" dir="rtl">{r.singular?.ar ?? ''}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
      {(metadata?.extra_modules ?? []).map((m) => (
        <Section key={m.key} title={<>New module <span className="mono">{m.key}</span></>}
          meta={`${m.label?.en ?? ''} / ${m.plural?.en ?? ''}${m.section?.en ? ` · section ${m.section.en}` : ''}`} flush>
          <div className="table-wrap"><FieldsTable fields={m.fields ?? []} /></div>
        </Section>
      ))}
      {metadata?.extra_fields && Object.entries(metadata.extra_fields).map(([module, group]) => (
        <Section key={module} title={<>Fields added to <span className="mono">{module}</span></>} meta={group.section?.en ?? ''} flush>
          <div className="table-wrap"><FieldsTable fields={group.fields ?? []} /></div>
        </Section>
      ))}
      <Section title="Pack document" meta="as shipped (code-as-data)">
        <pre className="pre-json">{JSON.stringify(doc, null, 2)}</pre>
      </Section>
    </>
  );
}
